import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "../../Reveal";
import { useI18n } from "../../../i18n/LanguageContext";
import { SITE } from "../../../content/site";
import { routePath } from "../../../lib/routes";
import { track, EVENTS } from "../../../lib/analytics";

export function FeaturedModule({ f, i }) {
  const { lang } = useI18n();
  return (
    <section data-testid={`home-featured-${i}`} className="relative min-h-[86vh] overflow-hidden md:min-h-[96vh]">
      <img src={f.img} alt={f.title} loading="lazy" width="1920" height="1280"
        className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/25" />
      <div className="relative mx-auto flex min-h-[86vh] max-w-[1400px] flex-col items-start justify-end px-6 pb-16 md:min-h-[96vh] md:px-12 md:pb-24 lg:items-center lg:text-center">
        <Reveal><p className="overline text-white/70">{f.overline}</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display mt-5 max-w-3xl text-3xl font-light leading-[1.05] tracking-tightest text-white sm:text-4xl lg:text-6xl">{f.title}</h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-white/80 md:text-lg">{f.text}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <Link to={routePath(f.route, lang)} data-testid={`home-featured-cta-${i}`}
            onClick={() => track(EVENTS.CTA_CLICK, { cta: `home_featured_${f.route}` })}
            className="ep-btn ep-btn-light mt-9">
            {f.cta} <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function Developments({ standalone = false }) {
  const { lang } = useI18n();
  const h = (SITE[lang].home2 || SITE.en.home2);
  const railRef = React.useRef(null);
  const [canScroll, setCanScroll] = React.useState(false);
  React.useEffect(() => {
    const check = () => {
      const el = railRef.current;
      if (el) setCanScroll(el.scrollWidth > el.clientWidth + 8);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const slide = (dir) => railRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  return (
    <section data-testid="home-developments" className={standalone ? "pb-20 md:pb-24" : "bg-white py-16 md:py-24"}>
      {!standalone && (
        <div className="mx-auto mb-10 max-w-[1200px] px-6 text-center md:mb-12 md:px-8">
          <Reveal><p className="overline text-[#0e7c6b]">{h.devOverline}</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-4 text-2xl font-semibold tracking-tightest text-[#050505] md:text-4xl">{h.devTitle}</h2>
          </Reveal>
        </div>
      )}
      <div className="relative mx-auto max-w-[1200px]">
        {canScroll && (
          <>
            <button data-testid="dev-carousel-prev" onClick={() => slide(-1)} aria-label="Previous"
              className="absolute -left-1 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-black/10 bg-white shadow-md md:flex">
              <ArrowRight size={17} className="rotate-180 text-[#141514]" />
            </button>
            <button data-testid="dev-carousel-next" onClick={() => slide(1)} aria-label="Next"
              className="absolute -right-1 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-black/10 bg-white shadow-md md:flex">
              <ArrowRight size={17} className="text-[#141514]" />
            </button>
          </>
        )}
        <div ref={railRef} data-testid="dev-carousel"
          className="flex snap-x snap-mandatory overflow-x-auto px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="mx-auto flex w-max gap-4 md:gap-5">
            {h.developments.map((d, i) => (
              <motion.div key={d.name} initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}>
              <Link to={routePath(d.route, lang)} data-testid={`home-dev-${i}`}
                className="group flex h-[280px] w-[250px] shrink-0 snap-center flex-col overflow-hidden rounded-2xl border border-black/8 bg-white transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:border-black/12 hover:shadow-[0_24px_60px_rgba(0,0,0,0.12)] md:h-[320px] md:w-[290px]">
                <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 pt-4">
                  <img src={d.img} alt={d.name} loading="lazy"
                    className="max-h-full w-auto max-w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.07]" />
                </div>
                <div className="px-6 pb-6 pt-3 text-center">
                  <p className="text-[13px] font-light leading-snug text-[#5a5d58] md:text-sm">{d.tag}</p>
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#050505]">{d.name}</p>
                </div>
              </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
